import { store } from 'app/store';
import { fetchTenantUserPaginatedMemberships } from 'common/services/api/TenantUserMembership/TenantUserMembership.thunk';
import { fetchUserGroupsUsers } from 'common/services/api/UserGroupsUsers/userGroupsUsers.thunk';
import { Acl,License } from 'common/types/Entities';
import { dateToFrontEndFormat } from 'common/utils/dateHelpers';
import { sortByProp } from 'common/utils/SortArrayByPropName';

import {
    AclEntriesWithLicenseInfo,
    LicensesUsersData,
    LicensesUsersPromiseList,
    LicensesUsersResponse,
    ReportProductEntitlements,
    User,
    UserGroupsUsersData,
    UserGroupsUsersPromiseList,
    UserGroupsUsersResponse,
    UsersResponse } from './typeDefs';

const THE_NUMBER_OF_REQUESTS_TO_START_COUNT_FROM = 1;

export const isPromise = <T>(value: T | Promise<T>): value is Promise<T> => {
    return Boolean(value && typeof (value as Promise<T>).then === 'function');
};

export const _getUniqueUsers = (users: User[]): User[]  => {
    return users.reduce((acc: User[], user: User) => {
        const { firstName, lastName, email } = user;
        const isDuplicate = acc.some((userProp) =>
            userProp.firstName === firstName &&
            userProp.lastName === lastName &&
            userProp.email === email
        );
        if (!isDuplicate) {
            acc.push(user);
        }
        return acc;
    }, []);
};

export const _getAllUsersData = <T> (
    allUsers: UsersResponse<T>[]
): T[] => {
    let allFetchedUsersListsByData: T[] = [];

    for(const user of allUsers) {
        allFetchedUsersListsByData = allFetchedUsersListsByData.concat(user.data);
    }
    return allFetchedUsersListsByData;
};

export const _formatAllUserGroupsUsers = (
    allUsersByDataProp: UserGroupsUsersData[]
): User[] => {
    const flattenedUsersList: User[] = allUsersByDataProp.map(item => ({
        id: item.id,
        firstName: item.firstName || item.profile.firstName,
        lastName: item.lastName || item.profile.lastName,
        oasysId: item.oasysId,
        userId: item.userId,
        companyKey: item.companyKey || item.profile.companyKey || '',
        email: item.profile.email || '',
        startDate: item.startDate ? dateToFrontEndFormat(item.startDate) : '',
        endDate: item.endDate ? dateToFrontEndFormat(item.endDate) : ''
    }));
    return flattenedUsersList;
};

export const _formatAllLicensesUsers = (
    allUsersByDataProp: LicensesUsersData[]
): User[] => {
    const flattenedUsersList: User[] = allUsersByDataProp.map(item => ({
        id: item.userId,
        firstName: item.firstName || item.profile.firstName,
        lastName: item.lastName || item.profile.lastName,
        oasysId: item.oasysId ?? '',
        userId: item.userId,
        companyKey: item.profile.companyKey ??  '',
        email: item.profile.email ?? '',
        startDate: item.startDate ? dateToFrontEndFormat(item.startDate) : '',
        endDate: item.endDate ? dateToFrontEndFormat(item.endDate) : ''
    }));
    return flattenedUsersList;
};

export const _calculateRequestCountForUsersLimitGreaterThanUsersCount = <T>(
    userGroupUsers: UsersResponse<T>,
    defaultFetchCount: number,
    usersLimit: number
): number => {
    if(userGroupUsers.count <= usersLimit) {
        return Math.ceil(userGroupUsers.count / defaultFetchCount);
    } else {
        return Math.ceil(usersLimit / defaultFetchCount);
    }
};

export const _defineUsersRequestCount = <T>(
    userGroupUsers: UsersResponse<T>,
    defaultFetchCount: number,
    usersLimit?: number,
): number => {
    if(usersLimit) {
        return _calculateRequestCountForUsersLimitGreaterThanUsersCount(
            userGroupUsers,
            defaultFetchCount,
            usersLimit
        );
    }
    return Math.ceil(userGroupUsers.count / defaultFetchCount);
};

const _createUserGroupsUsersPromiseList = (
    userGroupUsersResponse: UserGroupsUsersResponse,
    userGroupsUsersPromiseList: UserGroupsUsersPromiseList[],
    defaultFetchCount: number,
    requestsCount: number,
    isActive?: boolean
) => {
    const { dispatch } = store;
    for(let i = 1; i < requestsCount; i++) {
        const requestParams = {
            tenantId: userGroupUsersResponse.requestPayload.tenantId || '',
            usergroupId: userGroupUsersResponse.requestPayload.userGroupId || '',
            isActive: isActive,
            skip: i * defaultFetchCount,
            take: defaultFetchCount,
        };
        userGroupsUsersPromiseList.push(
            dispatch(fetchUserGroupsUsers(requestParams))
                .unwrap()
        );
    }
};

const _createLicensesUsersPromiseList = (
    licenseUsersResponse: LicensesUsersResponse,
    licensesUsersPromiseList: LicensesUsersPromiseList[],
    defaultFetchCount: number,
    requestsCount: number
) => {
    const { dispatch } = store;
    for(let i = 1; i < requestsCount; i++) {
        const requestParams = {
            tenantId: licenseUsersResponse.requestPayload.tenantId ?? '',
            licenseId: licenseUsersResponse.requestPayload.licenseId ?? '',
            skip: i * defaultFetchCount,
            take: defaultFetchCount,
        };
        licensesUsersPromiseList.push(
            dispatch(fetchTenantUserPaginatedMemberships(requestParams))
                .unwrap()
        );
    }
};

const _createSingleUserGroupsUsersPromise = (
    userGroupUsersResponse: UserGroupsUsersResponse,
    userGroupsUsersPromiseList: UserGroupsUsersPromiseList[],
    defaultFetchCount: number,
    isActive?: boolean
) => {
    const { dispatch } = store;
    const requestParams = {
        tenantId: userGroupUsersResponse.requestPayload.tenantId || '',
        usergroupId: userGroupUsersResponse.requestPayload.userGroupId || '',
        isActive: isActive,
        skip: defaultFetchCount,
        take: defaultFetchCount,
    };
    userGroupsUsersPromiseList.push(
        dispatch(fetchUserGroupsUsers(requestParams))
            .unwrap()
    );
};

const _buildUserGroupsUsersChunkedPromiseList = (
    userGroupsUsersWithMoreUsersToFetch: UserGroupsUsersResponse[],
    defaultFetchCount: number,
    usersLimit?: number,
    isActive?: boolean
): UserGroupsUsersPromiseList[] => {
    const userGroupsUsersPromiseList: UserGroupsUsersPromiseList[] = [];

    for (const userGroupUsers of userGroupsUsersWithMoreUsersToFetch) {
        const requestsCount = _defineUsersRequestCount(
            userGroupUsers,
            defaultFetchCount,
            usersLimit
        );
        if(requestsCount > THE_NUMBER_OF_REQUESTS_TO_START_COUNT_FROM) {
            _createUserGroupsUsersPromiseList(
                userGroupUsers,
                userGroupsUsersPromiseList,
                defaultFetchCount,
                requestsCount,
                isActive
            );
        } else if (requestsCount === THE_NUMBER_OF_REQUESTS_TO_START_COUNT_FROM) {
            _createSingleUserGroupsUsersPromise(
                userGroupUsers,
                userGroupsUsersPromiseList,
                defaultFetchCount,
            );
        }

    }
    return userGroupsUsersPromiseList;
};

const _createSingleLicensesUsersPromise = (
    licenseUsersResponse: LicensesUsersResponse,
    licensesUsersPromiseList: LicensesUsersPromiseList[],
    defaultFetchCount: number,
    isActive?: boolean
) => {
    const { dispatch } = store;
    const requestParams = {
        tenantId: licenseUsersResponse.requestPayload.tenantId ?? '',
        licenseId: licenseUsersResponse.requestPayload.licenseId ?? '',
        isActive: isActive,
        skip: defaultFetchCount,
        take: defaultFetchCount,
    };
    licensesUsersPromiseList.push(
        dispatch(fetchTenantUserPaginatedMemberships(requestParams))
            .unwrap()
    );
};

const _buildLicensesUsersChunkedPromiseList = (
    licensesUsersWithMoreUsersToFetch: LicensesUsersResponse[],
    defaultFetchCount: number,
    usersLimit?: number
): LicensesUsersPromiseList[] => {
    const licensesUsersPromiseList: LicensesUsersPromiseList[] = [];

    for (const licenseUsers of licensesUsersWithMoreUsersToFetch) {
        const requestsCount = _defineUsersRequestCount(
            licenseUsers,
            defaultFetchCount,
            usersLimit
        );
        if(requestsCount > THE_NUMBER_OF_REQUESTS_TO_START_COUNT_FROM) {
            _createLicensesUsersPromiseList(
                licenseUsers,
                licensesUsersPromiseList,
                defaultFetchCount,
                requestsCount
            );
        } else if (requestsCount === THE_NUMBER_OF_REQUESTS_TO_START_COUNT_FROM) {
            _createSingleLicensesUsersPromise(
                licenseUsers,
                licensesUsersPromiseList,
                defaultFetchCount,
            );
        }

    }
    return licensesUsersPromiseList;
};

const _doMoreUsersToFetchExist = <T>(
    usersWithMoreUsersToFetch: T[],
): boolean => {
    return usersWithMoreUsersToFetch.length > 0;
};

const _getUserGroupsUsersWithMoreUsersToFetch = (
    fetchedUserGroupsUsers: UserGroupsUsersResponse[]
): UserGroupsUsersResponse[] => {
    return fetchedUserGroupsUsers.filter(item => item.count > item.taken);
};

const _getLicensesUsersWithMoreUsersToFetch = (
    fetchedUsers: LicensesUsersResponse[]
): LicensesUsersResponse[] => {
    return fetchedUsers.filter(item => item.count > item.taken);
};

const _handleComputeChunkedUserGroupsUsersRequests = (
    userGroupsUsersWithMoreUsersToFetch: UserGroupsUsersResponse[],
    defaultTake: number,
    usersLimit?: number,
    isActive?: boolean
): Promise<UserGroupsUsersResponse[]> | undefined => {
    if(_doMoreUsersToFetchExist(userGroupsUsersWithMoreUsersToFetch)) {
        const userGroupsUsersPromiseList = _buildUserGroupsUsersChunkedPromiseList(
            userGroupsUsersWithMoreUsersToFetch,
            defaultTake,
            usersLimit,
            isActive
        );
        return Promise.all(userGroupsUsersPromiseList)
            .then(restOfFetchedUserGroupsUsers => {
                return [
                    ...restOfFetchedUserGroupsUsers
                ];
            });
    }
};

const _handleComputeChunkedLicensesUsersRequests = (
    licensesUsersWithMoreUsersToFetch: LicensesUsersResponse[],
    defaultTake: number,
    usersLimit?: number
): Promise<LicensesUsersResponse[]> | undefined => {
    if(_doMoreUsersToFetchExist(licensesUsersWithMoreUsersToFetch)) {
        const licensesUsersPromiseList = _buildLicensesUsersChunkedPromiseList(
            licensesUsersWithMoreUsersToFetch,
            defaultTake,
            usersLimit
        );
        return Promise.all(licensesUsersPromiseList)
            .then(restOfFetchedUserGroupsUsers => {
                return [
                    ...restOfFetchedUserGroupsUsers
                ];
            });
    }
};

export const getUniqueUsersSortedByEmailWithoutLimitNumber = (
    fetchedUserGroupsUsers: UserGroupsUsersResponse[]
): User[] => {
    const allUsersList = _getAllUsersData(fetchedUserGroupsUsers);
    const formattedUsersList = _formatAllUserGroupsUsers(allUsersList);
    const sortedByEmailUsersList: User[] = sortByProp('email')(formattedUsersList);
    return _getUniqueUsers(sortedByEmailUsersList);
};

export const getLimitedNumberOfUniqueUsersSortedByEmail = (
    fetchedUserGroupsUsers: UserGroupsUsersResponse[],
    usersLimit: number,
): User[] => {
    const uniqueSortedUsers = getUniqueUsersSortedByEmailWithoutLimitNumber(fetchedUserGroupsUsers);
    return uniqueSortedUsers.slice(0, usersLimit);
};

export const getUniqueLicensesUsersSortedByEmailWithoutLimitNumber = (
    fetchedLicensesUsers: LicensesUsersResponse[]
): User[] => {
    const allUsersList = _getAllUsersData(fetchedLicensesUsers);
    const formattedUsersList = _formatAllLicensesUsers(allUsersList);
    const sortedByEmailUsersList: User[] = sortByProp('email')(formattedUsersList);
    return _getUniqueUsers(sortedByEmailUsersList);
};

export const getLimitedNumberOfUniqueLicensesUsersSortedByEmail = (
    fetchedLicensesUsers: LicensesUsersResponse[],
    usersLimit: number,
): User[] => {
    const uniqueSortedUsers = getUniqueLicensesUsersSortedByEmailWithoutLimitNumber(fetchedLicensesUsers);
    return uniqueSortedUsers.slice(0, usersLimit);
};

export const fetchAllUsers = (
    fetchedUserGroupsUsersOnFirstPageLoad: UserGroupsUsersResponse[],
    defaultTake: number,
    usersLimit?: number,
    isActive?: boolean
): Promise<UserGroupsUsersResponse[]> | undefined => {
    const userGroupsUsersWithMoreUsersToFetch = _getUserGroupsUsersWithMoreUsersToFetch(fetchedUserGroupsUsersOnFirstPageLoad);

    return _handleComputeChunkedUserGroupsUsersRequests(
        userGroupsUsersWithMoreUsersToFetch,
        defaultTake,
        usersLimit,
        isActive
    );
};

export const fetchAllLicensesUsers = (
    fetchedUsersOnFirstPageLoad: LicensesUsersResponse[],
    defaultTake: number,
    userLimit?: number
): Promise<LicensesUsersResponse[]> | undefined => {
    const licensesUsersWithMoreUsersToFetch = _getLicensesUsersWithMoreUsersToFetch(fetchedUsersOnFirstPageLoad);

    return _handleComputeChunkedLicensesUsersRequests(
        licensesUsersWithMoreUsersToFetch,
        defaultTake,
        userLimit
    );
};

export const _getNumberOfUsersToFetch = (userLimit: string, userLimitDisabled: boolean, defaultTake: number) => {
    const userLimitIsEmpty = userLimit === '';
    return userLimitDisabled || userLimitIsEmpty || !userLimitIsEmpty && Number(userLimit) > defaultTake
        ? defaultTake : Number(userLimit);
};

/*
* Product Entitlements helpers section
*/

export const _getProductEntitlementsDates = (userGroupAcl: Acl, licenseByAclEntry?: License) => {
    const aclStartDate = userGroupAcl?.startDate ?? '';
    const aclEndDate = userGroupAcl?.endDate ?? '';
    const licenseStartDate = licenseByAclEntry?.startDate ?? '';
    const licenseEndDate = licenseByAclEntry?.endDate ?? '';

    const productEntitlementsStartDates = {
        aclStartDate,
        licenseStartDate
    };
    const productEntitlementsEndDates = {
        aclEndDate,
        licenseEndDate
    };

    return {
        productEntitlementsStartDates,
        productEntitlementsEndDates,
    };
};

export const _formatProductEntitlementsStartDates = (
    productEntitlementsStartDates: {
        aclStartDate: string;
        licenseStartDate: string;
    }
): string => {
    const { aclStartDate, licenseStartDate } = productEntitlementsStartDates;

    if(aclStartDate && licenseStartDate) {
        const aclDate = new Date(aclStartDate);
        const licenseDate = new Date(licenseStartDate);

        if (licenseDate.getTime() > aclDate.getTime()) {
            return dateToFrontEndFormat(licenseStartDate);
        }
    }

    const initialAclStartDateToReturn = aclStartDate ? dateToFrontEndFormat(aclStartDate) : '';
    const initialLicenseStartDateToReturn = licenseStartDate ? dateToFrontEndFormat(licenseStartDate) : '';

    return initialAclStartDateToReturn || initialLicenseStartDateToReturn;
};

export const _formatProductEntitlementsEndDates = (
    productEntitlementsEndDates: {
        aclEndDate: string;
        licenseEndDate: string;
    }
): string => {
    const { aclEndDate, licenseEndDate } = productEntitlementsEndDates;

    if(aclEndDate && licenseEndDate) {
        const aclDate = new Date(aclEndDate);
        const licenseDate = new Date(licenseEndDate);

        if(licenseDate.getTime() < aclDate.getTime()) {
            return dateToFrontEndFormat(licenseEndDate);
        }
    }

    const initialAclEndDateToReturn = aclEndDate ? dateToFrontEndFormat(aclEndDate) : '';
    const initialLicenseEndDateToReturn = licenseEndDate ? dateToFrontEndFormat(licenseEndDate) : '';

    return initialAclEndDateToReturn || initialLicenseEndDateToReturn;
};

/* This function formats the access control list Start/End dates and adds license billing record.
* For the Start/End dates formatting it matches the ACL with the associated license and it keeps the later
* date for the Start date and the earlier date for the End date.
*/

export const _formatProductEntitlementsWithLicenseInfo = ({
    acls,
    licenses,
    isLicenseRequired
}: AclEntriesWithLicenseInfo): ReportProductEntitlements[] => {
    const productEntitlements = acls.map((aclEntry) => {
        const licenseByAclEntry = licenses.find((license) => license.id === aclEntry.licenseId);

        const {
            productEntitlementsStartDates,
            productEntitlementsEndDates
        } = _getProductEntitlementsDates(aclEntry, licenseByAclEntry);

        const formattedStartDate = _formatProductEntitlementsStartDates(productEntitlementsStartDates);
        const formattedEndDate = _formatProductEntitlementsEndDates(productEntitlementsEndDates);
        const licenseBillingRecord = isLicenseRequired ? licenseByAclEntry?.billingRecord : '';

        return {
            ...aclEntry,
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            licenseBillingRecord,
        };
    });

    return productEntitlements;
};
